import { Badge, Card, Text } from '@radix-ui/themes';
import clsx from 'clsx';
import type { ComponentProps } from 'react';
import { useEffect, useState } from 'react';
import type {
  ClosedMessageDocument,
  OpenMessageDocument,
  RecordingMessageDocument,
  TAnyClientQuestionType,
} from '@hubertai/screening-question-types';
import type { ScoreInterviewContextType } from '../scoring.context';
import { EPreferredScoringLanguage, useScoreInterview } from '../scoring.context';

interface Props extends ComponentProps<'div'> {
  question?: TAnyClientQuestionType;
  message: ClosedMessageDocument | OpenMessageDocument | RecordingMessageDocument;
}

const HubertMessage = ({ question, message, ...props }: Props) => {
  const { preferredLanguage } = useScoreInterview() as ScoreInterviewContextType;
  const [texts, setTexts] = useState<string[] | undefined>([]);

  useEffect(() => {
    if (!preferredLanguage || preferredLanguage === EPreferredScoringLanguage.Original) {
      setTexts(message.texts ?? []);
      return;
    }

    const expectedLanguage = message.expected_language;

    if (preferredLanguage === EPreferredScoringLanguage.English) {
      // If the expected and preferred language is English, return the texts
      if (['en-gb', 'en-us'].includes(expectedLanguage)) {
        return setTexts(message.texts);
      }

      // If the preferred language is English, but preferred language is not English, check
      // if the question has the preferred language (en-gb)
      if (question && question.available_languages.includes('en-gb')) {
        const intro = question.intro['en-gb'] ?? [];
        const text = question.text['en-gb'] ?? [];
        const outro = question.outro['en-gb'] ?? [];
        return setTexts([...intro, ...text, ...outro]);
      }
    }
    // otherwise fallback to original/default values
    return setTexts(message.texts);
  }, [preferredLanguage]);

  // console.log('<HubertMessage /> message preferredLanguage', message, preferredLanguage)
  return (
    <div {...props} className={clsx('flex max-w-[80%] flex-col gap-1 p-px')}>
      <div className="flex flex-row justify-start gap-2">
        {message.text_type ? (
          <Badge
            variant="soft"
            color="blue"
            size="1"
            className="w-fit"
            style={{
              fontSize: '8px',
              fontWeight: 500,
              letterSpacing: '0.3px',
              textTransform: 'uppercase',
              opacity: 0.8,
            }}
          >
            {message.text_type.split('_').join(' ')}
          </Badge>
        ) : null}

        {message.channel && message.channel.length ? (
          <Badge
            variant="soft"
            color="blue"
            size="1"
            className="w-fit"
            style={{
              fontSize: '8px',
              fontWeight: 500,
              letterSpacing: '0.3px',
              textTransform: 'uppercase',
              opacity: 0.8,
            }}
          >
            {message.channel.split('_').join(' ')}
          </Badge>
        ) : null}
      </div>
      <Card>
        {texts &&
          texts.map((text: string, idx: number) => {
            return (
              <Text key={idx} as="div" size="2" color="gray">
                <div dangerouslySetInnerHTML={{ __html: text }} />
              </Text>
            );
          })}
      </Card>
    </div>
  );
};

export default HubertMessage;
